/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { createRoot, type Root } from 'react-dom/client';

import { ComponentIcon } from '../components/shared/ComponentIcon';
import { getModalFocusReturnTarget } from '../components/shared/modal-focus-return.utils';
import { DEFAULT_LANGUAGE, type SupportedLanguage } from './language.utils';
import '../components/shared/QubeProFeedback.css';

export type QubeProFeedbackVariant = 'info' | 'warning' | 'danger' | 'success' | 'error';

export interface QubeProFeedbackBaseOptions {
  language?: SupportedLanguage;
  title?: string;
  eyebrow?: string;
  message: React.ReactNode;
  details?: React.ReactNode;
  variant?: QubeProFeedbackVariant;
  iconName?: string;
}

export interface QubeProConfirmOptions extends QubeProFeedbackBaseOptions {
  confirmLabel?: string;
  cancelLabel?: string;
  closeOnOverlayClick?: boolean;
}

export interface QubeProDeleteConfirmOptions extends Omit<QubeProConfirmOptions, 'variant' | 'message'> {
  message?: React.ReactNode;
  recordName?: string | null;
}

export interface QubeProNoticeOptions extends QubeProFeedbackBaseOptions {
  actionLabel?: string;
}

type FeedbackTextKey =
  | 'confirmTitle'
  | 'deleteTitle'
  | 'warningTitle'
  | 'infoTitle'
  | 'successTitle'
  | 'errorTitle'
  | 'deleteEyebrow'
  | 'deleteGenericMessage'
  | 'deleteRecordMessage'
  | 'confirm'
  | 'cancel'
  | 'delete'
  | 'ok'
  | 'deleteDescription';

const feedbackTexts: Record<FeedbackTextKey, Record<SupportedLanguage, string>> = {
  confirmTitle: {
    tr: 'İşlemi onaylayın',
    en: 'Confirm action',
    ar: 'تأكيد الإجراء',
    de: 'Aktion bestätigen',
    fr: 'Confirmer l’action',
    es: 'Confirmar acción',
    it: 'Conferma azione',
    ru: 'Подтвердите действие',
    zh: '确认操作',
    ja: '操作を確認',
  },
  deleteTitle: {
    tr: 'Kaydı sil',
    en: 'Delete record',
    ar: 'حذف السجل',
    de: 'Datensatz löschen',
    fr: 'Supprimer l’enregistrement',
    es: 'Eliminar registro',
    it: 'Elimina record',
    ru: 'Удалить запись',
    zh: '删除记录',
    ja: 'レコードを削除',
  },
  deleteEyebrow: {
    tr: 'Silme işlemi',
    en: 'Delete action',
    ar: 'إجراء حذف',
    de: 'Löschvorgang',
    fr: 'Action de suppression',
    es: 'Acción de eliminación',
    it: 'Azione di eliminazione',
    ru: 'Удаление',
    zh: '删除操作',
    ja: '削除操作',
  },
  deleteGenericMessage: {
    tr: 'Bu kayıt silinsin mi?',
    en: 'Delete this record?',
    ar: 'هل تريد حذف هذا السجل؟',
    de: 'Diesen Datensatz löschen?',
    fr: 'Supprimer cet enregistrement ?',
    es: '¿Eliminar este registro?',
    it: 'Eliminare questo record?',
    ru: 'Удалить эту запись?',
    zh: '删除此记录？',
    ja: 'このレコードを削除しますか？',
  },
  deleteRecordMessage: {
    tr: '{record} silinsin mi?',
    en: 'Delete {record}?',
    ar: 'هل تريد حذف {record}؟',
    de: '{record} löschen?',
    fr: 'Supprimer {record} ?',
    es: '¿Eliminar {record}?',
    it: 'Eliminare {record}?',
    ru: 'Удалить {record}?',
    zh: '删除 {record}？',
    ja: '{record}を削除しますか？',
  },
  warningTitle: {
    tr: 'Dikkat',
    en: 'Attention',
    ar: 'تنبيه',
    de: 'Achtung',
    fr: 'Attention',
    es: 'Atención',
    it: 'Attenzione',
    ru: 'Внимание',
    zh: '注意',
    ja: '注意',
  },
  infoTitle: {
    tr: 'Bilgilendirme',
    en: 'Information',
    ar: 'معلومات',
    de: 'Information',
    fr: 'Information',
    es: 'Información',
    it: 'Informazione',
    ru: 'Информация',
    zh: '信息',
    ja: '情報',
  },
  successTitle: {
    tr: 'İşlem tamamlandı',
    en: 'Action completed',
    ar: 'اكتمل الإجراء',
    de: 'Aktion abgeschlossen',
    fr: 'Action terminée',
    es: 'Acción completada',
    it: 'Azione completata',
    ru: 'Действие выполнено',
    zh: '操作已完成',
    ja: '操作が完了しました',
  },
  errorTitle: {
    tr: 'İşlem tamamlanamadı',
    en: 'Action could not be completed',
    ar: 'تعذر إكمال الإجراء',
    de: 'Aktion konnte nicht abgeschlossen werden',
    fr: 'L’action n’a pas pu être terminée',
    es: 'No se pudo completar la acción',
    it: 'Impossibile completare l’azione',
    ru: 'Не удалось выполнить действие',
    zh: '无法完成操作',
    ja: '操作を完了できませんでした',
  },
  confirm: {
    tr: 'Onayla',
    en: 'Confirm',
    ar: 'تأكيد',
    de: 'Bestätigen',
    fr: 'Confirmer',
    es: 'Confirmar',
    it: 'Conferma',
    ru: 'Подтвердить',
    zh: '确认',
    ja: '確認',
  },
  cancel: {
    tr: 'Vazgeç',
    en: 'Cancel',
    ar: 'إلغاء',
    de: 'Abbrechen',
    fr: 'Annuler',
    es: 'Cancelar',
    it: 'Annulla',
    ru: 'Отмена',
    zh: '取消',
    ja: 'キャンセル',
  },
  delete: {
    tr: 'Sil',
    en: 'Delete',
    ar: 'حذف',
    de: 'Löschen',
    fr: 'Supprimer',
    es: 'Eliminar',
    it: 'Elimina',
    ru: 'Удалить',
    zh: '删除',
    ja: '削除',
  },
  ok: {
    tr: 'Tamam',
    en: 'OK',
    ar: 'حسنًا',
    de: 'OK',
    fr: 'OK',
    es: 'Aceptar',
    it: 'OK',
    ru: 'OK',
    zh: '确定',
    ja: 'OK',
  },
  deleteDescription: {
    tr: 'İşlem tamamlandığında kayıt listede görünmez.',
    en: 'After this action, the record will no longer appear in the list.',
    ar: 'بعد هذا الإجراء، لن يظهر السجل في القائمة.',
    de: 'Nach dieser Aktion wird der Datensatz nicht mehr in der Liste angezeigt.',
    fr: 'Après cette action, l’enregistrement n’apparaîtra plus dans la liste.',
    es: 'Después de esta acción, el registro ya no aparecerá en la lista.',
    it: 'Dopo questa azione, il record non sarà più visibile nell’elenco.',
    ru: 'После этого действия запись больше не будет отображаться в списке.',
    zh: '执行此操作后，该记录将不再显示在列表中。',
    ja: 'この操作後、レコードは一覧に表示されなくなります。',
  },
};

export function getQubeProFeedbackText(key: FeedbackTextKey, language: SupportedLanguage = DEFAULT_LANGUAGE): string {
  return feedbackTexts[key]?.[language] || feedbackTexts[key]?.[DEFAULT_LANGUAGE] || key;
}

function resolveDefaultTitle(variant: QubeProFeedbackVariant, language: SupportedLanguage): string {
  if (variant === 'danger') return getQubeProFeedbackText('deleteTitle', language);
  if (variant === 'warning') return getQubeProFeedbackText('warningTitle', language);
  if (variant === 'success') return getQubeProFeedbackText('successTitle', language);
  if (variant === 'error') return getQubeProFeedbackText('errorTitle', language);
  return getQubeProFeedbackText('infoTitle', language);
}

function resolveIconName(variant: QubeProFeedbackVariant, explicit?: string): string {
  if (explicit) return explicit;
  if (variant === 'danger') return 'delete';
  if (variant === 'warning') return 'warning';
  if (variant === 'success') return 'check';
  if (variant === 'error') return 'warning';
  return 'info';
}

interface FeedbackDialogProps extends QubeProFeedbackBaseOptions {
  mode: 'confirm' | 'notice';
  confirmLabel: string;
  cancelLabel?: string;
  closeOnOverlayClick: boolean;
  onResolve: (confirmed: boolean) => void;
}

function QubeProFeedbackDialog({
  mode,
  language = DEFAULT_LANGUAGE,
  title,
  eyebrow,
  message,
  details,
  variant = 'info',
  iconName,
  confirmLabel,
  cancelLabel,
  closeOnOverlayClick,
  onResolve,
}: FeedbackDialogProps) {
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  const resolvedTitle = title || resolveDefaultTitle(variant, language);
  const resolvedIcon = useMemo(() => resolveIconName(variant, iconName), [variant, iconName]);

  useEffect(() => {
    const target = mode === 'confirm' ? cancelRef.current : confirmRef.current;
    target?.focus();
  }, [mode]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onResolve(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onResolve]);

  const confirmClassName = [
    variant === 'danger' || variant === 'error'
      ? 'btn-danger qp-feedback-dialog__confirm-danger'
      : 'btn-primary',
  ].join(' ');

  return (
    <div
      className="qp-feedback-overlay"
      onMouseDown={(event) => {
        if (closeOnOverlayClick && event.target === event.currentTarget) onResolve(false);
      }}
    >
      <section
        className={`qp-feedback-dialog qp-feedback-dialog--${variant}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="qp-feedback-title"
        aria-describedby="qp-feedback-message"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="qp-feedback-dialog__header">
          <div className="qp-feedback-dialog__icon" aria-hidden="true">
            <ComponentIcon name={resolvedIcon} size={22} />
          </div>
          <div className="qp-feedback-dialog__title-wrap">
            {eyebrow && <p className="qp-feedback-dialog__eyebrow">{eyebrow}</p>}
            <h2 className="qp-feedback-dialog__title" id="qp-feedback-title">
              {resolvedTitle}
            </h2>
          </div>
        </div>

        <div className="qp-feedback-dialog__body">
          <div className="qp-feedback-dialog__message" id="qp-feedback-message">
            {message}
          </div>
          {details && <div className="qp-feedback-dialog__details">{details}</div>}
        </div>

        <div className="qp-feedback-dialog__actions">
          {mode === 'confirm' && (
            <button
              ref={cancelRef}
              type="button"
              className="btn-secondary"
              onClick={() => onResolve(false)}
            >
              {cancelLabel}
            </button>
          )}
          <button
            ref={confirmRef}
            type="button"
            className={confirmClassName}
            onClick={() => onResolve(true)}
          >
            {confirmLabel}
          </button>
        </div>
      </section>
    </div>
  );
}

function renderFeedbackDialog(options: FeedbackDialogProps): Promise<boolean> {
  if (typeof document === 'undefined') return Promise.resolve(false);

  const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const returnTarget = opener ? getModalFocusReturnTarget(opener) ?? opener : null;

  const container = document.createElement('div');
  container.setAttribute('data-qubepro-feedback-root', 'true');
  document.body.appendChild(container);

  let root: Root | null = createRoot(container);
  const cleanup = () => {
    const activeRoot = root;
    root = null;
    activeRoot?.unmount();
    container.remove();
    // The action toolbar can disappear while the notice owns focus. Use its
    // persistent record target, and never focus a detached or hidden surface.
    if (returnTarget?.isConnected && returnTarget.getClientRects().length
      && !returnTarget.closest('[inert], [aria-hidden="true"]')
      && !(returnTarget instanceof HTMLButtonElement && returnTarget.disabled)) {
      returnTarget.focus({ preventScroll: true });
    }
  };

  return new Promise<boolean>((resolve) => {
    const handleResolve = (confirmed: boolean) => {
      cleanup();
      resolve(confirmed);
    };

    root?.render(
      <QubeProFeedbackDialog
        {...options}
        onResolve={handleResolve}
      />,
    );
  });
}

export function showQubeProConfirmDialog(options: QubeProConfirmOptions): Promise<boolean> {
  const language = options.language || DEFAULT_LANGUAGE;
  const variant = options.variant || 'warning';

  if (typeof document === 'undefined') {
    const fallbackMessage =
      typeof options.message === 'string'
        ? options.message
        : options.title || getQubeProFeedbackText('confirmTitle', language);
    return Promise.resolve(typeof window !== 'undefined' ? window.confirm(fallbackMessage) : false);
  }

  return renderFeedbackDialog({
    ...options,
    language,
    variant,
    mode: 'confirm',
    confirmLabel:
      options.confirmLabel ||
      (variant === 'danger'
        ? getQubeProFeedbackText('delete', language)
        : getQubeProFeedbackText('confirm', language)),
    cancelLabel: options.cancelLabel || getQubeProFeedbackText('cancel', language),
    closeOnOverlayClick: options.closeOnOverlayClick ?? false,
    onResolve: () => undefined,
  });
}

export function showQubeProDeleteConfirmDialog(options: QubeProDeleteConfirmOptions): Promise<boolean> {
  const language = options.language || DEFAULT_LANGUAGE;
  const recordName = options.recordName?.trim();
  const message = options.message || (
    recordName
      ? getQubeProFeedbackText('deleteRecordMessage', language).replace('{record}', recordName)
      : getQubeProFeedbackText('deleteGenericMessage', language)
  );

  return showQubeProConfirmDialog({
    ...options,
    language,
    variant: 'danger',
    title: options.title || getQubeProFeedbackText('deleteTitle', language),
    eyebrow: options.eyebrow || getQubeProFeedbackText('deleteEyebrow', language),
    message,
    details: options.details,
    confirmLabel: options.confirmLabel || getQubeProFeedbackText('delete', language),
    cancelLabel: options.cancelLabel || getQubeProFeedbackText('cancel', language),
    iconName: options.iconName || 'delete',
  });
}

export function showQubeProNotice(options: QubeProNoticeOptions): Promise<void> {
  const language = options.language || DEFAULT_LANGUAGE;
  const variant = options.variant || 'info';

  if (typeof document === 'undefined') {
    if (typeof window !== 'undefined' && typeof options.message === 'string') window.alert(options.message);
    return Promise.resolve();
  }

  return renderFeedbackDialog({
    ...options,
    language,
    variant,
    mode: 'notice',
    confirmLabel: options.actionLabel || getQubeProFeedbackText('ok', language),
    closeOnOverlayClick: true,
    onResolve: () => undefined,
  }).then(() => undefined);
}
